import React, { useState, useEffect } from 'react';
import { X, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

export default function MessageNotificationPopup({ notification, onDismiss }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      setIsExiting(false);
      setExpanded(false);
      // Auto-dismiss after 15 seconds (only if not expanded)
      const timer = setTimeout(() => {
        if (!expanded) handleDismiss();
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      setExpanded(false);
      if (onDismiss) onDismiss();
    }, 300);
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  if (!isVisible || !notification) return null;

  const messageBody = notification.message || notification.body || notification.content || '';

  return (
    <div
      className={`fixed top-4 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)] transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'
      }`}
      style={{ animation: isExiting ? undefined : 'slideInRight 0.3s ease-out' }}
      data-testid="message-notification-popup"
    >
      <div className="bg-background border border-primary/20 rounded-xl shadow-2xl overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">
                  {notification.title || notification.subject || 'New Message'}
                </h4>
                <p className="text-[11px] text-muted-foreground">
                  {notification.from_name || notification.sender_name || 'System'}
                  {notification.exercise_name && (
                    <span className="ml-1 opacity-70">in {notification.exercise_name}</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="dismiss-msg-notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Message preview / full content */}
          <div className="ml-[3.125rem] mb-3">
            <div
              className={`text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap ${
                expanded ? '' : 'line-clamp-3'
              }`}
              data-testid="msg-notification-content"
            >
              {messageBody}
            </div>
          </div>

          {/* Actions */}
          <div className="ml-[3.125rem] flex items-center gap-2">
            {messageBody.length > 120 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleToggleExpand}
                className="h-8 text-xs gap-1.5"
                data-testid="expand-msg-notification"
              >
                {expanded ? (
                  <><ChevronUp className="w-3.5 h-3.5" /> Show Less</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5" /> Read Full Message</>
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="h-8 text-xs text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>

        {/* Progress bar showing auto-dismiss countdown */}
        {!expanded && (
          <div className="h-0.5 bg-muted">
            <div
              className="h-full bg-blue-500/50"
              style={{
                animation: 'shrinkWidth 15s linear forwards',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes shrinkWidth {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
